import * as E from 'fp-ts/lib/Either';
import * as t from 'io-ts';
import uuid from 'uuid-random';

import {AppAction} from '../../src/store/appActions';
import {Dispatch, StateGetter} from '../../src/store/appStore';
import {OptimisticUpdater} from '../../src/store/optimisticUpdates/optimisticUpdatesState';

/*
 * Types.
 */

/** Base shape of a task. */
interface TaskBase<N extends string, P> {
  /** UUID of the task. */
  id: string;
  /** Name of the task, used as an io-ts tag to decode and rehydrate the task. */
  name: N;
  /** Payload of the task. */
  payload: P;
}

/** Serialized task. */
export interface PrimitiveTask<N extends string, PO> extends TaskBase<N, PO> {}

/** Deserialized task. */
export interface Task<N extends string, PA> extends TaskBase<N, PA> {
  optimisticUpdater: OptimisticUpdater;
  performer: TaskPerformer;
}

export type TaskPerformer = (
  dispatch: Dispatch,
  getState: StateGetter,
  finishOptimisticUpdate: (resultAction?: AppAction) => void,
) => Promise<AppAction | void>;

/** Runtime type props of a task. */
interface TaskTypeProps<N extends string, PA, PO = PA> extends t.Props {
  id: t.StringC;
  name: t.LiteralC<N>;
  payload: t.Type<PA, PO>;
  optimisticUpdater: t.UnknownC;
  performer: t.UnknownC;
}

/*
 * Runtime types.
 */

/** Runtime type responsible for creating/serializing/deserializing tasks. */
export class TaskType<N extends string, PA, PO = PA> extends t.InterfaceType<
  TaskTypeProps<N, PA, PO>,
  Task<N, PA>,
  PrimitiveTask<N, PO>,
  unknown
> {
  constructor(
    name: N,
    payloadType: t.Type<PA, PO>,
    createOptimisticUpdater: (payload: PA, id: string) => OptimisticUpdater,
    createPerformer: (payload: PA) => TaskPerformer,
  ) {
    const primitiveTaskType = t.exact(
      t.type({
        id: t.string,
        name: t.literal(name),
        payload: payloadType,
      }),
    );

    const taskType = t.type({
      id: t.string,
      name: t.literal(name),
      payload: payloadType,
      optimisticUpdater: t.unknown,
      performer: t.unknown,
    });

    const rehydrateTask = (task: TaskBase<N, PA>): Task<N, PA> => ({
      ...task,
      optimisticUpdater: createOptimisticUpdater(task.payload, task.id),
      performer: createPerformer(task.payload),
    });

    super(
      `${name}Task`,
      (u): u is Task<N, PA> => taskType.is(u),
      (u, c) =>
        E.either.chain(
          // Type-check the primitive shape.
          primitiveTaskType.validate(u, c),
          // Re-create the functions.
          (task) => t.success(rehydrateTask(task)),
        ),
      (a) => primitiveTaskType.encode(a),
      taskType.props,
    );

    this.createTask = (payload) =>
      rehydrateTask({
        id: uuid(),
        name,
        payload,
      });
  }

  readonly createTask: (payload: PA) => Task<N, PA>;
}

/*
 * Helpers.
 */

export interface TaskTypeOptions<N extends string, PA, PO = PA> {
  name: N;
  payloadType: t.Type<PA, PO>;
  createOptimisticUpdater: (payload: PA, id: string) => OptimisticUpdater;
  createPerformer: (payload: PA) => TaskPerformer;
}

export function createTaskType<N extends string, PA, PO = PA>({
  name,
  payloadType,
  createOptimisticUpdater,
  createPerformer,
}: TaskTypeOptions<N, PA, PO>): TaskType<N, PA, PO> {
  return new TaskType(name, payloadType, createOptimisticUpdater, createPerformer);
}

export function createTask<N extends string, PA, PO = PA>(
  taskType: TaskType<N, PA, PO>,
  payload: PA,
): Task<N, PA> {
  return taskType.createTask(payload);
}
